/**
 * TileStore: the client's copy of the canvas.
 *
 * - Live tiles are fetched once (GET /v1/tiles/{tile}) and then kept fresh by
 *   WebSocket rooms (spec §4.5): snapshot on subscribe, then diffs.
 * - Playback mode swaps the renderer's data for a given day (spec §2.3).
 * - Local (optimistic) paints are applied immediately and reconciled when
 *   the server responds.
 */
import { tileFromToken, tileKey, tileToken, type TileRef } from '@drawearth/geo';
import { decodeTileMessage, type LivePainter, type PresenceInfo } from '@drawearth/shared';
import type { ApiClient } from './api.ts';
import type { RendererClient } from '../render/client.ts';

export type StoreMode = { kind: 'live' } | { kind: 'playback'; date: string; excludeAgents: boolean };

interface Entry {
  token: string;
  ref: TileRef;
  key: number;
  pixels: Uint8Array | null | undefined;
  loading: Promise<void> | null;
  ws: WebSocket | null;
  wanted: boolean;
  retry: number;
  pingTimer: ReturnType<typeof setInterval> | null;
}

export type ChangeListener = (keys: number[]) => void;
export type PresenceListener = (key: number, painters: LivePainter[]) => void;
const PRESENCE_TTL_MS = 10 * 60_000;

export class TileStore {
  private entries = new Map<number, Entry>();
  private playback = new Map<string, Uint8Array | null>();
  private listeners = new Set<ChangeListener>();
  private presenceListeners = new Set<PresenceListener>();
  /** Live painters per tile key (opted-in users and agents), by user id. */
  private presence = new Map<number, Map<number, LivePainter>>();
  private mode: StoreMode = { kind: 'live' };
  private disposed = false;

  constructor(
    private api: ApiClient,
    private renderer: RendererClient,
    private wsBase: string,
    private maxSubscriptions = 64,
  ) {}

  getMode(): StoreMode {
    return this.mode;
  }

  onChange(cb: ChangeListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(keys: number[]): void {
    for (const l of this.listeners) l(keys);
  }

  onPresence(cb: PresenceListener): () => void {
    this.presenceListeners.add(cb);
    return () => this.presenceListeners.delete(cb);
  }

  /** Live painters in a tile, freshest first, expired ones dropped. */
  presenceFor(key: number, nowMs = Date.now()): LivePainter[] {
    const m = this.presence.get(key);
    if (!m) return [];
    for (const [uid, p] of m) if (nowMs - p.ts > PRESENCE_TTL_MS) m.delete(uid);
    return [...m.values()].sort((a, b) => b.ts - a.ts);
  }

  private applyPresence(e: Entry, painters: LivePainter[]): void {
    let m = this.presence.get(e.key);
    if (!m) {
      m = new Map();
      this.presence.set(e.key, m);
    }
    for (const p of painters) m.set(p.userId, p);
    for (const l of this.presenceListeners) l(e.key, this.presenceFor(e.key));
  }

  private entry(ref: TileRef): Entry {
    const key = tileKey(ref);
    let e = this.entries.get(key);
    if (!e) {
      e = { token: tileToken(ref), ref, key, pixels: undefined, loading: null, ws: null, wanted: false, retry: 0, pingTimer: null };
      this.entries.set(key, e);
    }
    return e;
  }

  entryFor(token: string): Entry {
    return this.entry(tileFromToken(token));
  }

  /** Pixels for the current mode (undefined = not loaded). */
  getPixels(ref: TileRef): Uint8Array | null | undefined {
    if (this.mode.kind === 'playback') return this.playback.get(this.playbackKey(tileToken(ref)));
    return this.entries.get(tileKey(ref))?.pixels;
  }

  colourAt(ref: TileRef, idx: number): number {
    const px = this.getPixels(ref);
    return px ? px[idx] : 0;
  }

  private playbackKey(token: string): string {
    const m = this.mode as { date: string; excludeAgents: boolean };
    return `${m.date}:${m.excludeAgents ? 1 : 0}:${token}`;
  }

  /** Make sure the renderer has data for this tile in the current mode. */
  async ensure(ref: TileRef): Promise<void> {
    const e = this.entry(ref);
    if (this.mode.kind === 'playback') {
      const pk = this.playbackKey(e.token);
      if (this.playback.has(pk)) return;
      const mode = this.mode;
      let px: Uint8Array | null = null;
      try {
        px = await this.api.playback(e.token, mode.date, mode.excludeAgents);
        if (px.every((v) => v === 0)) px = null;
      } catch {
        px = null;
      }
      this.playback.set(pk, px);
      if (this.mode === mode) this.renderer.setTile(e.key, px);
      return;
    }
    if (e.pixels !== undefined) return;
    if (e.loading) return e.loading;
    e.loading = (async () => {
      try {
        const px = await this.api.tilePixels(e.token);
        if (e.pixels === undefined) {
          e.pixels = px;
          if (this.mode.kind === 'live') this.renderer.setTile(e.key, px);
        }
      } catch {
        // leave undefined; will retry on next ensure
      } finally {
        e.loading = null;
      }
    })();
    return e.loading;
  }

  async ensureAll(refs: TileRef[]): Promise<void> {
    await Promise.all(refs.map((r) => this.ensure(r)));
  }

  /** Switch between live and playback data. */
  setMode(mode: StoreMode): void {
    this.mode = mode;
    this.renderer.clearTiles();
    if (mode.kind === 'live') {
      for (const e of this.entries.values()) if (e.pixels !== undefined) this.renderer.setTile(e.key, e.pixels);
    } else {
      for (const [pk, px] of this.playback) {
        if (pk.startsWith(`${mode.date}:${mode.excludeAgents ? 1 : 0}:`)) this.renderer.setTile(this.entryFor(pk.split(':')[2]).key, px);
      }
    }
    this.emit([]);
  }

  /** Optimistic local paint (live mode only). */
  applyLocal(ref: TileRef, ops: Array<{ idx: number; colour: number }>): void {
    if (this.mode.kind !== 'live') return;
    const e = this.entry(ref);
    if (!e.pixels) e.pixels = new Uint8Array(65536);
    for (const op of ops) e.pixels[op.idx] = op.colour;
    this.renderer.applyDiff(e.key, ops);
    this.emit([e.key]);
  }

  /** Force a reload of a live tile (after a rejected draw). */
  async reload(ref: TileRef): Promise<void> {
    const e = this.entry(ref);
    e.pixels = undefined;
    await this.ensure(ref);
    this.emit([e.key]);
  }

  /** Keep WebSocket rooms open for these tiles (capped) and close the rest. */
  subscribe(refs: TileRef[]): void {
    const wanted = new Set(refs.slice(0, this.maxSubscriptions).map((r) => tileKey(r)));
    for (const e of this.entries.values()) {
      if (!wanted.has(e.key) && e.wanted) {
        e.wanted = false;
        this.closeWs(e);
      }
    }
    for (const r of refs.slice(0, this.maxSubscriptions)) {
      const e = this.entry(r);
      if (!e.wanted) {
        e.wanted = true;
        e.retry = 0;
        this.openWs(e);
      }
    }
  }

  private openWs(e: Entry): void {
    if (this.disposed || e.ws || !e.wanted) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${this.wsBase}/v1/tiles/${e.token}/ws`);
    } catch {
      return;
    }
    ws.binaryType = 'arraybuffer';
    e.ws = ws;
    ws.onmessage = async (ev: MessageEvent) => {
      if (typeof ev.data === 'string') return; // pong / info
      try {
        const msg = await decodeTileMessage(ev.data as ArrayBuffer);
        if (msg.type === 'snapshot') {
          const empty = msg.pixels.every((v) => v === 0);
          e.pixels = empty ? null : msg.pixels;
          if (this.mode.kind === 'live') this.renderer.setTile(e.key, e.pixels);
          this.emit([e.key]);
        } else if (msg.type === 'diff') {
          if (!e.pixels) e.pixels = new Uint8Array(65536);
          for (const d of msg.entries) e.pixels[d.idx] = d.colour;
          if (this.mode.kind === 'live') this.renderer.applyDiff(e.key, msg.entries);
          this.emit([e.key]);
        } else if (msg.type === 'info') {
          const info = msg.info as Partial<PresenceInfo>;
          if (info && info.type === 'presence' && Array.isArray(info.painters)) this.applyPresence(e, info.painters);
        }
      } catch {
        // ignore malformed frames
      }
    };
    ws.onopen = () => {
      e.retry = 0;
      e.pingTimer = setInterval(() => {
        try {
          ws.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // closed
        }
      }, 30_000);
    };
    ws.onclose = () => {
      if (e.pingTimer) clearInterval(e.pingTimer);
      e.pingTimer = null;
      e.ws = null;
      if (e.wanted && !this.disposed) {
        const delay = Math.min(30_000, 1000 * Math.pow(2, e.retry++));
        setTimeout(() => this.openWs(e), delay);
      }
    };
    ws.onerror = () => {
      // onclose follows
    };
  }

  private closeWs(e: Entry): void {
    if (e.pingTimer) clearInterval(e.pingTimer);
    e.pingTimer = null;
    const ws = e.ws;
    e.ws = null;
    try {
      ws?.close();
    } catch {
      // ignore
    }
  }

  subscribedCount(): number {
    let n = 0;
    for (const e of this.entries.values()) if (e.ws) n++;
    return n;
  }

  dispose(): void {
    this.disposed = true;
    for (const e of this.entries.values()) {
      e.wanted = false;
      this.closeWs(e);
    }
  }
}
